import { Typography, Paper, Box, Stack, useTheme } from "@mui/material"
import { useNavigate, useLocation, useOutletContext } from "react-router-dom"
import React, { useState, useCallback, useEffect } from "react"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as CalcIcon } from "src/v2/assets/icons/calc-icon.svg"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import CaracteristicasSection from "../componentes/CaracteristicasSection"
import DatasSection from "../componentes/DatasSection"
import JurosCorrecaoSection from "../componentes/JurosCorrecaoSection"
import FatorArredondamentoSection from "../componentes/FatorArredondamentoSection"
import FormActions from "../../components/FormActions"
import { caracteristicasFields } from "../componentes/CaracteristicasSection/constants"
import { datasFields } from "../componentes/DatasSection/constants"
import { jurosCorrecaoFields } from "../componentes/JurosCorrecaoSection/constants"
import { fatorArredondamentoFields } from "../componentes/FatorArredondamentoSection/constants"
import { caracteristicasSectionKeys } from "../componentes/CaracteristicasSection/section-keys"
import { datasSectionKeys } from "../componentes/DatasSection/section-keys"
import { jurosCorrecaoSectionKeys } from "../componentes/JurosCorrecaoSection/section-keys"
import { fatorArredondamentoSectionKeys } from "../componentes/FatorArredondamentoSection/section-keys"
import { getInvalidFields } from "../../utils/section-validity"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import MotorParametrosService from "src/services/motor-parametros-service"
import MotorService from "src/services/motor-service"
import { mapCadastroTemporarioParametros, mapCaracteristicaParametros, mapParametrosToForm } from "../../utils/map-temporary-cadastro"
import Loader from "src/v2/components/Loader/loader"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"


const fieldsMap = {
    caracteristicas: caracteristicasFields,
    datas: datasFields,
    jurosCorrecao: jurosCorrecaoFields,
    fatorArredondamento: fatorArredondamentoFields
}

export const ParametrosPage = () => {
    const parametrosService = new MotorParametrosService()
    const motorService = new MotorService()
    const theme = useTheme()
    const navigate = useNavigate()
    const location = useLocation()
    const navigationState = location.state

    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)

    const { setIsProducao } = useOutletContext() || {}

    const [parametrosInfo, setParametrosInfo] = useState({
        id: null,
        idCaracteristica: null,
        cetip: null,
        versaoDocumento: null
    })

    const isPublished = parametrosInfo?.id != null

    const sectionScrollMargin = 16

    const generateInitialState = (fields) => {
        return fields.reduce((acc, field) => {
            acc[field.key] = field.defaultValue ?? ""
            return acc
        }, {})
    }

    const [formData, setFormData] = useState({
        caracteristicas: generateInitialState(caracteristicasFields),
        datas: generateInitialState(datasFields),
        jurosCorrecao: generateInitialState(jurosCorrecaoFields),
        fatorArredondamento: generateInitialState(fatorArredondamentoFields)
    })

    const setSectionField = (section, key, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [key]: value,
            },
        }))
    }


    const validateForm = () => {
        const invalidFields = [
            ...getInvalidFields({ sectionName: "caracteristicas", keys: caracteristicasSectionKeys, state: formData.caracteristicas, fieldsMap }),
            ...getInvalidFields({ sectionName: "datas", keys: datasSectionKeys, state: formData.datas, fieldsMap }),
            ...getInvalidFields({ sectionName: "jurosCorrecao", keys: jurosCorrecaoSectionKeys, state: formData.jurosCorrecao, fieldsMap }),
            ...getInvalidFields({ sectionName: "fatorArredondamento", keys: fatorArredondamentoSectionKeys, state: formData.fatorArredondamento, fieldsMap }),
        ]

        if (invalidFields.length > 0) {
            dispatchSnackbar(`Campos obrigatórios não preenchidos: ${invalidFields.join(", ")}`, "warning")
            return false
        }

        return true
    }

    useEffect(() => {
        if (!navigationState) return

        const origem = navigationState.origem

        if (origem === "ver") {
            const { parametro, caracteristica } = navigationState

            const cetip = caracteristica?.CETIP ?? parametro?.CETIP
            const versao = caracteristica?.VersaoDocumento ?? parametro?.VersaoDocumento

            setParametrosInfo({
                id: parametro?.ID ?? null,
                idCaracteristica: caracteristica?.ID ?? null,
                cetip,
                versaoDocumento: versao
            })

            if (parametro) {
                const parametroMapeado = mapParametrosToForm(parametro)
                setFormData(prev => ({
                    ...prev,
                    caracteristicas: { ...prev.caracteristicas, ...parametroMapeado.caracteristicas },
                    datas: { ...prev.datas, ...parametroMapeado.datas },
                    jurosCorrecao: { ...prev.jurosCorrecao, ...parametroMapeado.jurosCorrecao },
                    fatorArredondamento: { ...prev.fatorArredondamento, ...parametroMapeado.fatorArredondamento }
                }))
            }

            return
        }

        const { ID, CETIP, VersaoDocumento, Caracteristica } = navigationState

        if (!ID) return

        setParametrosInfo({
            id: null,
            idCaracteristica: ID,
            cetip: CETIP,
            versaoDocumento: VersaoDocumento
        })

        if (Caracteristica) {
            const caracteristicaMapeada = mapCaracteristicaParametros(Caracteristica)
            setFormData(prev => ({
                ...prev,
                caracteristicas: {
                    ...prev.caracteristicas,
                    ...caracteristicaMapeada,
                    cetip: CETIP ?? "",
                    versaoDocumento: VersaoDocumento ?? ""
                }
            }))
        }

    }, [navigationState])

    const handleSave = async () => {
        if (!validateForm()) return
        try {
            setIsSaving(true)

            const payload = mapCadastroTemporarioParametros(formData)
            const { CETIP, VersaoDocumento } = payload

            let caracteristicaID = parametrosInfo.idCaracteristica

            if (!caracteristicaID) {
                const response = await motorService.loadCadastros(
                    CETIP,
                    VersaoDocumento
                )

                const caracteristica = response?.body?.[0]

                if (!caracteristica?.ID) {
                    dispatchSnackbar(
                        "Não existe cadastro de características para o CETIP e versão informados.",
                        "error"
                    )
                    return
                }

                caracteristicaID = caracteristica.ID
            }

            const saveResponse =
                await parametrosService.createCadastroTemporarioParametros(
                    caracteristicaID,
                    payload
                )

            const mensagem = saveResponse?.body?.mensagem ?? "Parâmetros salvos com sucesso."
            dispatchSnackbar(mensagem, "success")

            setParametrosInfo(prev => ({
                ...prev,
                idCaracteristica: caracteristicaID,
                cetip: CETIP,
                versaoDocumento: VersaoDocumento
            }))


        } catch (error) {
            console.error(error)
            dispatchSnackbar("Erro ao salvar parâmetros.", "error")
        } finally {
            setIsSaving(false)
        }
    }

    const handlePublish = async () => {
        if (!validateForm()) return
        try {
            setIsSaving(true)

            const payload = mapCadastroTemporarioParametros(formData)

            const hasProducaoId = parametrosInfo?.id != null

            const response = hasProducaoId
                ? await parametrosService.updateCadastroParametros(
                    parametrosInfo.idCaracteristica,
                    payload
                )
                : await parametrosService.createCadastroParametros(
                    parametrosInfo.idCaracteristica,
                    payload
                )

            const savedId = response?.body?.ID

            setParametrosInfo(prev => ({
                ...prev,
                id: savedId
            }))

            dispatchSnackbar(
                hasProducaoId
                    ? "Cadastro atualizado em produção com sucesso."
                    : "Cadastro publicado com sucesso.",
                "success"
            )

            setIsProducao(true)

        } catch (error) {
            console.error("Erro ao publicar cadastro", error)

            const data =
                error?.response?.body ||
                error?.response?.data ||
                error?.response?.text

            const message =
                data?.detail ||
                data?.message ||
                error?.message ||
                "Falha ao publicar cadastro."

            dispatchSnackbar(message, "error")

        } finally {
            setIsSaving(false)
            setPublishConfirmOpen(false)
        }
    }

    const handleDelete = useCallback(async () => {
        try {
            setIsDeleting(true)

            if (!parametrosInfo?.idCaracteristica) {
                navigate(-1)
                return
            }

            const isProducao = parametrosInfo?.id != null

            if (isProducao) {
                await parametrosService.deleteCadastroParametros(
                    parametrosInfo.idCaracteristica
                )
            } else {
                await parametrosService.deleteCadastroTemporarioParametros(
                    parametrosInfo.idCaracteristica
                )
            }

            // recarrega características e limpa seções de parâmetros
            const responseCaracteristica = await motorService.getCadastro(
                parametrosInfo.idCaracteristica
            )
            const caracteristica = responseCaracteristica?.body

            if (caracteristica) {
                const caracteristicaMapeada = mapCaracteristicaParametros(caracteristica.Caracteristica)
                setFormData({
                    caracteristicas: {
                        ...generateInitialState(caracteristicasFields),
                        ...caracteristicaMapeada,
                        cetip: caracteristica.CETIP ?? "",
                        versaoDocumento: caracteristica.VersaoDocumento ?? ""
                    },
                    datas: generateInitialState(datasFields),
                    jurosCorrecao: generateInitialState(jurosCorrecaoFields),
                    fatorArredondamento: generateInitialState(fatorArredondamentoFields)
                })
            }

            setParametrosInfo(prev => ({ ...prev, id: null }))
            setIsProducao(false)

            dispatchSnackbar(
                isProducao
                    ? "Cadastro deletado com sucesso."
                    : "Cadastro temporário deletado com sucesso.",
                "success"
            )

        } catch (error) {
            console.error("Erro ao deletar cadastro", error)
            dispatchSnackbar("Erro ao deletar cadastro.", "error")
        } finally {
            setIsDeleting(false)
            setDeleteConfirmOpen(false)
        }
    }, [parametrosInfo, navigate, motorService])

    return (
        <>
            {(isSaving || isDeleting) && (
                <LoaderOverlay>
                    <Loader />
                </LoaderOverlay>
            )}

            <Stack spacing={2} sx={{ mt: 3, pb: 2 }}>
                <Paper
                    square
                    sx={{
                        p: 4,
                        backgroundColor: "background.paper",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5
                    }}
                >
                    <Typography
                        sx={{
                            mb: 3,
                            fontWeight: 400,
                            fontSize: "14px",
                            textTransform: 'uppercase',
                            color: 'white.main'
                        }}>
                        Projetar
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2
                        }}>
                        <Typography sx={{ color: 'white.main', maxWidth: '70%' }}>
                            Com base nos Parâmetros abaixo, gostaria que o sistema projete o fluxo dos Eventos Ordinários
                        </Typography>

                        <Box>
                            <ButtonWithIcon icon={<CalcIcon />} size="md">
                                PROJETAR
                            </ButtonWithIcon>
                        </Box>
                    </Box>
                </Paper>

                <Box
                    sx={{
                        marginTop: { xs: -4, md: -14.5 },
                        paddingBottom: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        px: { xxxs: 3, md: 3, lg: 0 },
                    }}
                >
                    <div data-section="caracteristicas" style={{ scrollMarginTop: sectionScrollMargin }}>
                        <CaracteristicasSection
                            state={formData.caracteristicas}
                            setSectionField={setSectionField}
                            defaultOpen={true} // aparece aberto
                        />
                    </div>

                    <div data-section="datas" style={{ scrollMarginTop: sectionScrollMargin }}>
                        <DatasSection
                            state={formData.datas}
                            setSectionField={setSectionField}
                        />
                    </div>

                    <div data-section="jurosCorrecao" style={{ scrollMarginTop: sectionScrollMargin }}>
                        <JurosCorrecaoSection
                            state={formData.jurosCorrecao}
                            setSectionField={setSectionField}
                        />
                    </div>

                    <div data-section="fatorArredondamento" style={{ scrollMarginTop: sectionScrollMargin }}>
                        <FatorArredondamentoSection
                            state={formData.fatorArredondamento}
                            setSectionField={setSectionField}
                        />
                    </div>

                    <Box sx={{ mx: { xxxs: -3, md: -3, lg: 0 }, mt: 2 }}>
                        <FormActions
                            showBack
                            onBack={() => navigate(-1)}

                            onDelete={() => setDeleteConfirmOpen(true)}
                            deleteOpen={deleteConfirmOpen}
                            setDeleteOpen={setDeleteConfirmOpen}
                            onConfirmDelete={handleDelete}

                            showSave={!isPublished}
                            onSave={!isSaving ? handleSave : undefined}

                            onPublish={() => setPublishConfirmOpen(true)}
                            publishOpen={publishConfirmOpen}
                            setPublishOpen={setPublishConfirmOpen}
                            onConfirmPublish={handlePublish}
                            publishLabel={isPublished ? "SALVAR E PUBLICAR" : "PUBLICAR"}
                            publishIcon={<CheckIcon />}

                            theme={theme}
                        />
                    </Box>
                </Box>
            </Stack>
        </>
    )
}